import { z } from "@reflag/shared/utils/zod";

import { APIEmptyResponse, APIResponse } from "./api";
import { redirectUrlSchema } from "./schemas/redirectUrlSchema";

export interface LinearConnection {
  id: string;
  appId: string;
  organizationId: string;
  organizationName: string;
  needsReauthorization: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const LinearTeamSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

export type LinearTeam = z.infer<typeof LinearTeamSchema>;

export const LinearAgentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  displayName: z.string().min(1),
  email: z.string().email(),
  avatarUrl: z.string().nullish(),
});

export type LinearAgent = z.infer<typeof LinearAgentSchema>;

export const LinearIssueTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
  delegateId: z.string().nullable(),
});
export type LinearIssueTemplate = z.infer<typeof LinearIssueTemplateSchema>;

export const LinearTemplateParamsSchema = z.object({
  orgId: z.string(),
  templateId: z.string().uuid(),
});
export const SetLinearTemplateDelegateSchema = z.object({
  delegateId: z.string().uuid(),
});
export type CreatedLinearIssueTemplate = {
  template: LinearIssueTemplate;
  editUrl: string;
};

export const LinearCodingSessionTrustedSourceSchema = z.object({
  enabled: z.boolean(),
  disabledReason: z
    .enum([
      "CODING_SESSIONS_DISABLED",
      "TRUSTED_EXTERNAL_SOURCES_UNAVAILABLE",
      "APP_NOT_TRUSTED",
    ])
    .nullish(),
});

export type LinearCodingSessionTrustedSource = z.infer<
  typeof LinearCodingSessionTrustedSourceSchema
>;

export const LinearArchiveGuideOptionsSchema = z.object({
  workspaceUrlKey: z.string(),
  codingSessionTrustedSource: LinearCodingSessionTrustedSourceSchema.optional(),
  teams: z.array(LinearTeamSchema),
  agents: z.array(LinearAgentSchema),
  templates: z.array(LinearIssueTemplateSchema),
});

export type LinearArchiveGuideOptions = z.infer<
  typeof LinearArchiveGuideOptionsSchema
>;

export const LinearTrackingEntitySchema = z.object({
  type: z.enum(["issue", "project"]).describe("Type of linear entity"),
  id: z.string().min(1).describe("ID of the linear issue or project"),
  projectId: z.string().min(1).optional().describe("ID of the linear project"),
  identifier: z.string().optional().describe("Identifier of the linear issue"),
  title: z.string().min(1).describe("Title of the linear issue or project"),
  priority: z
    .number()
    .min(0)
    .max(4)
    .describe("Priority of the linear issue or project"),
  url: z.url().optional().describe("URL of the linear issue or project"),
  icon: z.string().nullish().describe("Icon of the linear project"),
  color: z.string().nullish().describe("Color of the linear project"),
});

export type LinearTrackingEntity = z.infer<typeof LinearTrackingEntitySchema>;

export const ConnectToLinearQuerySchema = z
  .object({
    appId: z.string(),
    redirectUri: redirectUrlSchema,
  })
  .strict();

export type ConnectToLinearQueryType = z.infer<
  typeof ConnectToLinearQuerySchema
>;

export const PatchLinearConnectionSchema = z
  .object({
    appId: z.string(),
  })
  .strict();

export type PatchLinearConnection = z.infer<typeof PatchLinearConnectionSchema>;

export const ConnectToLinearResponseQuerySchema = z
  .object({
    code: z.string().optional(),
    error: z.string().optional(),
    state: z.string().min(1),
  })
  .refine(
    ({ code, error }) =>
      (typeof code === "string") !== (typeof error === "string"),
    {
      message: "Exactly one of 'code' or 'error' must be present.",
      path: ["code", "error"],
    },
  );

export type ConnectToLinearResponseQueryType = z.infer<
  typeof ConnectToLinearResponseQuerySchema
>;

export const LinearEntitiesQuerySchema = z
  .object({
    searchTerm: z.string().optional(),
    maxResults: z.coerce.number<number>().min(1).max(50).default(10),
  })
  .strict();

export type LinearEntitiesQuery = z.input<typeof LinearEntitiesQuerySchema>;

export interface LinearConnectionAPI {
  "/orgs/:orgId/linear/connection": {
    GET: {
      response: APIResponse<LinearConnection | null>;
      params: { orgId: string };
    };
    DELETE: {
      response: APIEmptyResponse;
      params: { orgId: string };
    };
    PATCH: {
      response: APIResponse<LinearConnection>;
      params: { orgId: string };
      body: PatchLinearConnection;
    };
  };
  "/orgs/:orgId/linear/connection/search-entities": {
    GET: {
      response: APIResponse<LinearTrackingEntity[]>;
      params: { orgId: string };
      query: LinearEntitiesQuery;
    };
  };
  "/orgs/:orgId/linear/connection/issue-templates": {
    POST: {
      params: { orgId: string };
      body: Record<string, never>;
      response: APIResponse<CreatedLinearIssueTemplate>;
    };
  };
  "/orgs/:orgId/linear/connection/issue-templates/:templateId": {
    GET: {
      params: z.infer<typeof LinearTemplateParamsSchema>;
      response: APIResponse<LinearIssueTemplate>;
    };
    PATCH: {
      params: z.infer<typeof LinearTemplateParamsSchema>;
      body: z.infer<typeof SetLinearTemplateDelegateSchema>;
      response: APIResponse<LinearIssueTemplate>;
    };
  };
  "/orgs/:orgId/linear/connection/archive-guide-options": {
    GET: {
      response: APIResponse<LinearArchiveGuideOptions>;
      params: { orgId: string };
    };
  };
  "/orgs/:orgId/linear/connect": {
    GET: {
      params: { orgId: string };
      query: ConnectToLinearQueryType;
    };
  };
}
